/**
 * Simulated "resultado del match" file for the bulk-assignment wizard.
 *
 * The prototype does not parse the uploaded file (there is no parsing library
 * and no backend). Instead it builds the lines the external company *would*
 * have delivered, using the real applications and evaluators in the mock store,
 * so that the preview the admin verifies is exactly what ends up in the lists
 * after applying.
 *
 * It is deterministic: same store, same file. And it deliberately seeds one
 * instance of every case in the issue catalogue, so a demo tells the whole
 * story instead of only the happy path.
 */

import { ImportFileLine } from '../types/bulkAssignment';
import { db } from './store';
import { StoredApplication, StoredUser } from './seed';

const ASSIGNABLE_STATUSES = ['admissible', 'under_evaluation', 'evaluated'];

/** Statuses that consume a slot of the evaluator's contest quota. */
const QUOTA_STATUSES = [
  'pending',
  'accepted_not_started',
  'in_progress',
  'completed',
];

/** Stable 32-bit hash so the generated file never changes between reloads. */
const hash = (value: string): number => {
  let h = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
};

/**
 * Confidence for a pair, skewed the way a real match engine behaves: most pairs
 * land high, a third medium, and only about one in eight comes out weak.
 */
const confidenceFor = (folio: string, email: string): number => {
  const bucket = hash(`${folio}|${email}`) % 100;
  const spread = hash(`c|${folio}|${email}`) % 100;
  if (bucket < 12) return Math.round((0.3 + spread * 0.0024) * 100) / 100; // 0.30–0.54
  if (bucket < 42) return Math.round((0.55 + spread * 0.0024) * 100) / 100; // 0.55–0.79
  return Math.round((0.8 + spread * 0.0019) * 100) / 100; // 0.80–0.99
};

const emailsInTeam = (app: StoredApplication): string[] => {
  const team = (app.sectionAnswers as any)?.PROJECT_TEAM;
  if (!team) return [];
  const members = [
    team.principalInvestigator,
    ...(Array.isArray(team.coInvestigators) ? team.coInvestigators : []),
  ];
  return members
    .filter((m: any) => m && typeof m.email === 'string' && m.email)
    .map((m: any) => m.email.toLowerCase());
};

const teamMemberOf = (app: StoredApplication): { name: string; email: string } | null => {
  const team = (app.sectionAnswers as any)?.PROJECT_TEAM;
  const cois = Array.isArray(team?.coInvestigators) ? team.coInvestigators : [];
  const member = cois.find((c: any) => c?.email && c?.name);
  return member ? { name: member.name, email: member.email } : null;
};

/**
 * Builds the lines of the simulated result file for a contest.
 *
 * The general rule is two evaluators per admissible application, allocated to
 * whoever has the most quota left so the load spreads evenly; on top of that a
 * handful of scripted rows reproduce every issue the wizard can report.
 */
export const buildSimulatedFile = (
  templateId: string | null
): ImportFileLine[] => {
  const contestApps = db.applications.filter(
    (a) => !templateId || a.formTemplateId === templateId
  );
  const admissible = contestApps
    .filter((a) => a.status === 'admissible')
    .sort((a, b) => a.applicationNumber.localeCompare(b.applicationNumber));

  const excludedIds = new Set(
    db.contestExclusions
      .filter((e) => !templateId || e.templateId === templateId)
      .map((e) => e.evaluatorId)
  );

  const allEvaluators = db.users
    .filter((u) => u.role === 'evaluator')
    .sort((a, b) => a.name.localeCompare(b.name));

  // Quota already consumed before this import.
  const baseLoad = new Map<string, number>();
  allEvaluators.forEach((e) => {
    baseLoad.set(
      e.id,
      db.assignments.filter(
        (a) => a.evaluatorId === e.id && QUOTA_STATUSES.includes(a.status)
      ).length
    );
  });

  // Three of the busiest evaluators are left out of the match on purpose, so
  // the "evaluador sin ninguna postulación" case always has an example.
  const byLoadDesc = [...allEvaluators].sort(
    (a, b) => (baseLoad.get(b.id) ?? 0) - (baseLoad.get(a.id) ?? 0)
  );
  const unmatchedIds = new Set(byLoadDesc.slice(0, 3).map((e) => e.id));

  const pool = allEvaluators.filter(
    (e) => !unmatchedIds.has(e.id) && !excludedIds.has(e.id)
  );

  // Applications the file simply does not mention.
  const omittedIds = new Set(admissible.slice(-2).map((a) => a.id));
  const matched = admissible.filter((a) => !omittedIds.has(a.id));

  const planned = new Map<string, number>();
  const takenFor = (id: string) => (planned.get(id) ?? 0) + (baseLoad.get(id) ?? 0);

  const pickEvaluators = (app: StoredApplication, count: number): StoredUser[] => {
    const teamEmails = new Set(emailsInTeam(app));
    const candidates = pool
      .filter((e) => !teamEmails.has(e.email.toLowerCase()))
      .sort((a, b) => {
        const diff = takenFor(a.id) - takenFor(b.id);
        if (diff !== 0) return diff;
        return hash(app.id + a.id) - hash(app.id + b.id);
      });
    const chosen = candidates.slice(0, count);
    chosen.forEach((e) => planned.set(e.id, (planned.get(e.id) ?? 0) + 1));
    return chosen;
  };

  const lineFor = (folio: string, name: string, email: string): ImportFileLine => ({
    folio,
    evaluatorName: name,
    evaluatorEmail: email,
    confidence: confidenceFor(folio, email),
  });

  // ── Scripted cases, addressed by position so they stay stable ─────────────
  const at = (i: number): StoredApplication | undefined => matched[i];
  const scripted = {
    noCoverage: at(1),
    partial: [at(0), at(5), at(9)].filter(Boolean) as StoredApplication[],
    duplicate: at(3),
    excluded: at(4),
    notFound: at(6),
    selfAssignment: matched.find((a) => teamMemberOf(a) !== null),
    wrongStatus:
      contestApps.find((a) => a.status === 'not_admissible') ??
      contestApps.find((a) => !ASSIGNABLE_STATUSES.includes(a.status)),
  };

  const priorConflict = db.assignments.find(
    (a) =>
      (a.status === 'declined' || a.status === 'conflict_declared') &&
      matched.some((app) => app.id === a.applicationId)
  );
  const alreadyAssigned = db.assignments.find(
    (a) =>
      QUOTA_STATUSES.includes(a.status) &&
      matched.some((app) => app.id === a.applicationId)
  );

  const lines: ImportFileLine[] = [];

  matched.forEach((app) => {
    if (scripted.noCoverage?.id === app.id) {
      // Está en el archivo, pero ninguno de sus dos matches es importable.
      lines.push(lineFor(app.applicationNumber, 'Hernán Lizama Prat', 'h.lizama@externo.cl'));
      lines.push(lineFor(app.applicationNumber, 'Rosa Bahamondes Cid', 'r.bahamondes@externo.cl'));
      return;
    }

    const isPartial = scripted.partial.some((a) => a.id === app.id);
    // These four replace one of the two valid matches with a faulty one, so the
    // demo shows how a blocked match costs real coverage.
    const replacesOne =
      scripted.excluded?.id === app.id ||
      scripted.notFound?.id === app.id ||
      scripted.selfAssignment?.id === app.id ||
      (priorConflict && priorConflict.applicationId === app.id);

    const validCount = isPartial ? 1 : replacesOne ? 1 : 2;
    pickEvaluators(app, validCount).forEach((e) => {
      lines.push(lineFor(app.applicationNumber, e.name, e.email));
    });

    if (scripted.duplicate?.id === app.id && lines.length > 0) {
      const last = lines[lines.length - 1];
      lines.push(lineFor(app.applicationNumber, last.evaluatorName, last.evaluatorEmail));
    }
    if (scripted.excluded?.id === app.id) {
      const excluded = allEvaluators.find((e) => excludedIds.has(e.id));
      if (excluded) lines.push(lineFor(app.applicationNumber, excluded.name, excluded.email));
    }
    if (scripted.notFound?.id === app.id) {
      lines.push(
        lineFor(app.applicationNumber, 'Patricia Alcaíno Ruiz', 'p.alcaino@externo.cl')
      );
    }
    if (scripted.selfAssignment?.id === app.id) {
      const member = teamMemberOf(app);
      if (member) lines.push(lineFor(app.applicationNumber, member.name, member.email));
    }
    if (priorConflict && priorConflict.applicationId === app.id) {
      const evaluator = db.users.find((u) => u.id === priorConflict.evaluatorId);
      if (evaluator) lines.push(lineFor(app.applicationNumber, evaluator.name, evaluator.email));
    }
    if (alreadyAssigned && alreadyAssigned.applicationId === app.id) {
      const evaluator = db.users.find((u) => u.id === alreadyAssigned.evaluatorId);
      if (evaluator) lines.push(lineFor(app.applicationNumber, evaluator.name, evaluator.email));
    }
  });

  // Cuatro evaluadores quedan por sobre el cupo: la empresa externa no conoce la
  // carga que ya arrastran, así que el caso siempre existe en un archivo real.
  const overloadTargets = pool.slice(0, 4);
  overloadTargets.forEach((evaluator, i) => {
    matched.slice(20 + i * 5, 23 + i * 5).forEach((app) => {
      const already = lines.some(
        (l) => l.folio === app.applicationNumber && l.evaluatorEmail === evaluator.email
      );
      if (!already) lines.push(lineFor(app.applicationNumber, evaluator.name, evaluator.email));
    });
  });

  // Folios the contest does not know about.
  ['AV26901', 'AV26902'].forEach((folio, i) => {
    const evaluator = pool[i % pool.length];
    if (evaluator) lines.push(lineFor(folio, evaluator.name, evaluator.email));
  });

  // A postulación that changed status after the file was generated.
  if (scripted.wrongStatus) {
    pool.slice(0, 2).forEach((e) => {
      lines.push(lineFor(scripted.wrongStatus!.applicationNumber, e.name, e.email));
    });
  }

  return lines;
};
